(() => {
  const selector = '[data-load-more]';

  function updateLoadMoreState(wrapper, loaded, total) {
    const status = wrapper.querySelector('[data-load-more-status]');
    const progress = wrapper.querySelector('[data-load-more-progress]');
    const count = wrapper.querySelector('[data-load-more-count]');
    const remaining = Math.max(total - loaded, 0);
    const percentage = total ? Math.min((loaded / total) * 100, 100) : 100;

    const statusState = `${loaded}:${total}`;
    if (status && status.dataset.loadMoreState !== statusState) {
      const template = wrapper.dataset.statusTemplate || '__LOAD_MORE_LOADED__ of __LOAD_MORE_TOTAL__';
      const parts = template.split(/(__LOAD_MORE_LOADED__|__LOAD_MORE_TOTAL__)/g);
      const content = document.createDocumentFragment();

      parts.forEach(part => {
        if (part === '__LOAD_MORE_LOADED__' || part === '__LOAD_MORE_TOTAL__') {
          const value = document.createElement('strong');
          value.textContent = String(part === '__LOAD_MORE_LOADED__' ? loaded : total);
          content.appendChild(value);
        } else if (part) {
          content.appendChild(document.createTextNode(part));
        }
      });

      status.replaceChildren(content);
      status.dataset.loadMoreState = statusState;
    }
    if (progress) progress.style.width = `${percentage}%`;
    if (count) count.textContent = remaining ? `(${remaining})` : '';
  }

  function getVariantItems(grid = document.getElementById('collection-product-grid')) {
    if (!grid) return [];
    return Array.from(grid.children).filter(item => item.matches('.grid__item:not(.marketing-grid)'));
  }

  function updateVariantLoadMoreState(wrapper) {
    const items = getVariantItems();
    const loaded = items.filter(item => item.dataset.loadMoreHidden !== 'true').length;
    const total = Math.max(Number(wrapper.dataset.total) || 0, loaded);
    wrapper.dataset.loaded = String(loaded);
    wrapper.dataset.total = String(total);
    updateLoadMoreState(wrapper, loaded, total);
  }

  async function initializeVariantTotal(wrapper) {
    if (wrapper.dataset.variantTotalReady === 'true') return;
    if (wrapper.variantTotalPromise) return wrapper.variantTotalPromise;

    wrapper.variantTotalPromise = (async () => {
      const pageCount = Number(wrapper.dataset.totalPages) || 1;
      const currentPage = Number(wrapper.dataset.page) || 1;
      let total = getVariantItems().length;
      const pageUrls = [];

      for (let page = 1; page <= pageCount; page += 1) {
        if (page === currentPage) continue;
        const pageUrl = new URL(wrapper.dataset.nextUrl, window.location.href);
        pageUrl.searchParams.set('page', String(page));
        if (wrapper.dataset.sectionId) pageUrl.searchParams.set('section_id', wrapper.dataset.sectionId);
        pageUrls.push(pageUrl.toString());
      }

      const pageCounts = await Promise.all(pageUrls.map(async url => {
        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
          });
          if (!response.ok) throw new Error(`Variant count request failed: ${response.status}`);
          const html = await response.text();
          const pageDocument = new DOMParser().parseFromString(html, 'text/html');
          const pageGrid = pageDocument.getElementById('collection-product-grid');
          return getVariantItems(pageGrid).length;
        } catch (error) {
          console.warn('Unable to count grid cards on a collection page:', error);
          return null;
        }
      }));

      pageCounts.forEach(count => {
        if (count !== null) total += count;
      });
      wrapper.dataset.total = String(total);
      wrapper.dataset.variantTotalReady = 'true';
      updateVariantLoadMoreState(wrapper);
    })();

    return wrapper.variantTotalPromise;
  }

  function getStateStorageKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return `collection-load-more:${url.pathname}${url.search}`;
  }

  function saveLoadMoreState(wrapper) {
    try {
      sessionStorage.setItem(getStateStorageKey(), JSON.stringify({
        page: Number(wrapper.dataset.page) || 1,
        loaded: Number(wrapper.dataset.loaded) || 0,
        pageSize: Number(wrapper.dataset.pageSize) || 0,
        variantGrid: wrapper.dataset.variantGrid === 'true',
        visibleVariants: wrapper.dataset.variantGrid === 'true'
          ? getVariantItems().filter(item => !item.hidden).length
          : null,
      }));
    } catch (error) {
      console.warn('Unable to save collection load-more state:', error);
    }
  }

  async function restoreLoadMoreState(wrapper) {
    if (wrapper.dataset.restoreStarted === 'true') return;
    wrapper.dataset.restoreStarted = 'true';

    let savedState;
    try {
      savedState = JSON.parse(sessionStorage.getItem(getStateStorageKey()) || 'null');
    } catch (error) {
      console.warn('Unable to read collection load-more state:', error);
      return;
    }

    if (!savedState || savedState.pageSize !== Number(wrapper.dataset.pageSize)) return;

    const isVariantGrid = wrapper.dataset.variantGrid === 'true';
    if (savedState.variantGrid !== isVariantGrid) return;

    const targetPage = Math.max(Number(savedState.page) || 1, 1);
    const targetVisibleVariants = Number(savedState.visibleVariants) || 0;
    const currentPage = Number(wrapper.dataset.page) || 1;
    const currentVisibleVariants = getVariantItems().filter(item => item.dataset.loadMoreHidden !== 'true').length;
    if (targetPage <= currentPage && (!isVariantGrid || targetVisibleVariants <= currentVisibleVariants)) return;

    const grid = document.getElementById('collection-product-grid');
    const button = wrapper.querySelector('[data-load-more-button]');
    if (!grid) return;

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      while (
        ((Number(wrapper.dataset.page) || 1) < targetPage
          || (isVariantGrid && getVariantItems().length < targetVisibleVariants))
        && wrapper.dataset.nextUrl
      ) {
        const requestUrl = new URL(wrapper.dataset.nextUrl, window.location.href);
        if (wrapper.dataset.sectionId) requestUrl.searchParams.set('section_id', wrapper.dataset.sectionId);

        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`Load More restore failed: ${response.status}`);

        const html = await response.text();
        const pageDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = pageDocument.getElementById('collection-product-grid');
        const nextWrapper = pageDocument.querySelector(selector);
        if (!nextGrid) throw new Error('Load More restore response did not contain a product grid');

        const newItems = Array.from(nextGrid.children);
        newItems.forEach(item => grid.appendChild(item));
        if (isVariantGrid) {
          newItems.filter(item => item.matches('.grid__item:not(.marketing-grid)')).forEach(item => {
            item.hidden = true;
            item.dataset.loadMoreHidden = 'true';
          });
        }

        const newPage = (Number(wrapper.dataset.page) || 1) + 1;
        wrapper.dataset.page = String(newPage);
        wrapper.dataset.nextUrl = nextWrapper?.dataset.nextUrl || '';
        wrapper.dataset.loaded = String(Math.min(
          Number(wrapper.dataset.total) || 0,
          newPage * (Number(wrapper.dataset.pageSize) || 0)
        ));
      }

      if (isVariantGrid) {
        getVariantItems().forEach((item, index) => {
          if (index < targetVisibleVariants) {
            item.hidden = false;
            delete item.dataset.loadMoreHidden;
          } else {
            item.hidden = true;
            item.dataset.loadMoreHidden = 'true';
          }
        });
      }

      if (isVariantGrid) {
        wrapper.dataset.loaded = String(getVariantItems().filter(item => item.dataset.loadMoreHidden !== 'true').length);
      } else if (savedState.loaded) {
        wrapper.dataset.loaded = String(savedState.loaded);
      }
      updateLoadMoreState(
        wrapper,
        Number(wrapper.dataset.loaded) || 0,
        Number(wrapper.dataset.total) || 0
      );

      const hasHiddenVariantItems = isVariantGrid && getVariantItems().some(item => item.dataset.loadMoreHidden === 'true');
      wrapper.hidden = !wrapper.dataset.nextUrl && !hasHiddenVariantItems;
      if (window.theme?.reinitProductGridItem) window.theme.reinitProductGridItem(grid);
      if (window.byobGlobalHandler) window.byobGlobalHandler(true);
    } catch (error) {
      console.error('Unable to restore collection load-more state:', error);
    } finally {
      if (button) {
        button.removeAttribute('aria-busy');
        button.disabled = wrapper.hidden;
      }
    }
  }

  function prepareVariantItems(wrapper) {
    if (wrapper.dataset.variantGrid !== 'true') return;
    if (wrapper.dataset.variantGridPrepared === 'true') return;
    wrapper.dataset.variantGridPrepared = 'true';

    const pageSize = Number(wrapper.dataset.pageSize) || 0;
    getVariantItems().forEach((item, index) => {
      if (index >= pageSize && !item.hidden) {
        item.hidden = true;
        item.dataset.loadMoreHidden = 'true';
      }
    });
  }

  function revealVariantBatch(wrapper) {
    if (wrapper.dataset.variantGrid !== 'true') return false;

    const pageSize = Number(wrapper.dataset.pageSize) || 0;
    const hiddenItems = getVariantItems().filter(item => item.dataset.loadMoreHidden === 'true');
    hiddenItems.slice(0, pageSize).forEach(item => {
      item.hidden = false;
      delete item.dataset.loadMoreHidden;
    });

    return getVariantItems().some(item => item.dataset.loadMoreHidden === 'true');
  }

  async function loadMore(wrapper, button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const grid = document.getElementById('collection-product-grid');
    const nextUrl = wrapper.dataset.nextUrl;
    if (!grid) return;

    const isVariantGrid = wrapper.dataset.variantGrid === 'true';
    if (isVariantGrid) await initializeVariantTotal(wrapper);

    const total = Number(wrapper.dataset.total) || 0;
    const loaded = Number(wrapper.dataset.loaded) || 0;
    const pageSize = Number(wrapper.dataset.pageSize) || loaded;
    const currentPage = Number(wrapper.dataset.page) || 1;
    const sectionId = wrapper.dataset.sectionId;
    const requestUrl = new URL(nextUrl, window.location.href);
    if (sectionId) requestUrl.searchParams.set('section_id', sectionId);
    let firstNewItem;
    prepareVariantItems(wrapper);
    const hasHiddenVariantItems = revealVariantBatch(wrapper);
    if (isVariantGrid && hasHiddenVariantItems) {
      updateVariantLoadMoreState(wrapper);
      saveLoadMoreState(wrapper);
      button.focus();
      return;
    }
    if (!nextUrl) {
      wrapper.hidden = true;
      if (isVariantGrid) saveLoadMoreState(wrapper);
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Load More request failed: ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = documentFragment.getElementById('collection-product-grid');
      const nextWrapper = documentFragment.querySelector(selector);

      if (!nextGrid) throw new Error('Load More response did not contain a product grid');

      const newItems = Array.from(nextGrid.children);
      firstNewItem = newItems.find(item => !item.classList.contains('marketing-grid'));
      const fragment = document.createDocumentFragment();
      newItems.forEach(item => fragment.appendChild(item));
      grid.appendChild(fragment);
      if (isVariantGrid) {
        newItems.filter(item => item.matches('.grid__item:not(.marketing-grid)')).forEach(item => {
          item.hidden = true;
          item.dataset.loadMoreHidden = 'true';
        });
      }

      // Shopify paginates parent collection products, while variant PLPs can
      // render multiple cards per parent product. Advance by page, not by the
      // number of appended cards, so variant grids can load every page.
      const newPage = currentPage + 1;
      const newLoaded = isVariantGrid
        ? getVariantItems().filter(item => item.dataset.loadMoreHidden !== 'true').length
        : Math.min(total, newPage * pageSize);
      wrapper.dataset.loaded = String(newLoaded);
      wrapper.dataset.page = String(newPage);
      wrapper.dataset.nextUrl = nextWrapper?.dataset.nextUrl || '';
      updateLoadMoreState(wrapper, newLoaded, total);

      if (!wrapper.dataset.nextUrl || newLoaded >= total) {
        wrapper.hidden = true;
      } else {
        button.disabled = false;
      }

      if (wrapper.dataset.variantGrid === 'true') {
        revealVariantBatch(wrapper);
        updateVariantLoadMoreState(wrapper);
        if (wrapper.dataset.nextUrl || getVariantItems().some(item => item.dataset.loadMoreHidden === 'true')) {
          wrapper.hidden = false;
          button.disabled = false;
        } else {
          wrapper.hidden = true;
        }
      }

      saveLoadMoreState(wrapper);

      if (window.theme?.reinitProductGridItem) window.theme.reinitProductGridItem(grid);
      if (window.byobGlobalHandler) window.byobGlobalHandler(true);
      document.dispatchEvent(new CustomEvent('collection:load-more', {
        detail: {
          loaded: Number(wrapper.dataset.loaded) || 0,
          total: Number(wrapper.dataset.total) || 0,
        },
      }));
    } catch (error) {
      console.error('Unable to load more collection products:', error);
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
      if (wrapper.hidden) {
        firstNewItem?.querySelector('a, button')?.focus();
      } else {
        button.focus();
      }
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-load-more-button]');
    if (!button) return;

    const wrapper = button.closest(selector);
    if (!wrapper) return;
    event.preventDefault();
    loadMore(wrapper, button);
  });

  function initializeVariantGrids() {
    document.querySelectorAll(selector).forEach(wrapper => {
      prepareVariantItems(wrapper);
      updateLoadMoreState(
        wrapper,
        Number(wrapper.dataset.loaded) || 0,
        Number(wrapper.dataset.total) || 0
      );
      const ready = wrapper.dataset.variantGrid === 'true'
        ? initializeVariantTotal(wrapper)
        : Promise.resolve();
      ready.then(() => restoreLoadMoreState(wrapper));
    });
  }

  document.addEventListener('DOMContentLoaded', initializeVariantGrids);
  ['collection:updated', 'ajaxRendered', 'renderCollectionPage'].forEach(eventName => {
    document.addEventListener(eventName, initializeVariantGrids);
  });

  const collectionContent = document.getElementById('CollectionAjaxContent');
  if (collectionContent) {
    new MutationObserver(initializeVariantGrids).observe(collectionContent, {
      childList: true,
      subtree: true,
    });
  }
})();
